import React, { useEffect, useState, useCallback } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";
import { FaExclamationTriangle, FaEye, FaEnvelope } from "react-icons/fa";

interface BreachReport {
  breachId: string;
  detectedAt: string;
  indicators: {
    bulkDataExport?: boolean;
    rapidSequentialExports?: boolean;
    unauthorizedAccessAttempts?: number;
    unusualAccessPatterns?: boolean;
    suspiciousActivity?: boolean;
  };
  affectedUsers: Array<{
    userId?: string;
    email: string;
    storeId?: string;
  }>;
  severity: "low" | "medium" | "high" | "critical";
  status: "detected" | "investigating" | "contained" | "resolved";
  description: string;
}

const PlatformBreaches: React.FC = () => {
  const { isAuthenticated } = usePlatformAuth();
  const navigate = useNavigate();
  const [breaches, setBreaches] = useState<BreachReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(72);
  const [stats, setStats] = useState<any>(null);
  const [notifyingBreachId, setNotifyingBreachId] = useState<string | null>(null);

  const fetchBreaches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platformApiService.getBreaches({ hours, limit: 50 });
      setBreaches(response.data.breaches || []);
    } catch (err: any) {
      console.error("Error fetching breaches:", err);
      setError(err.response?.data?.error || "Failed to load breaches");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await platformApiService.getBreachStats({ hours });
      setStats(response.data);
    } catch (err: any) {
      console.error("Error fetching breach stats:", err);
    }
  }, [hours]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/platform/login", { replace: true });
      return;
    }

    fetchBreaches();
    fetchStats();
  }, [isAuthenticated, navigate, fetchBreaches, fetchStats]);

  const handleNotifyUsers = async (breachId: string) => {
    if (!window.confirm("Are you sure you want to notify all affected users about this breach? This action cannot be undone.")) {
      return;
    }

    try {
      setNotifyingBreachId(breachId);
      await platformApiService.notifyAffectedUsers(breachId);
      alert("Affected users have been notified successfully");
      fetchBreaches(); // Refresh the list
    } catch (err: any) {
      console.error("Error notifying users:", err);
      alert(err.response?.data?.error || "Failed to notify users");
    } finally {
      setNotifyingBreachId(null);
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-300";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "low":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getIndicatorsText = (indicators: any): string => {
    const parts: string[] = [];
    if (indicators.bulkDataExport) parts.push("Bulk Export");
    if (indicators.rapidSequentialExports) parts.push("Rapid Exports");
    if (indicators.unauthorizedAccessAttempts) parts.push(`${indicators.unauthorizedAccessAttempts} Unauthorized Attempts`);
    if (indicators.unusualAccessPatterns) parts.push("Unusual Patterns");
    if (indicators.suspiciousActivity) parts.push("Suspicious Activity");
    return parts.join(", ") || "Security Breach";
  };

  if (loading && breaches.length === 0) {
    return <div className="p-8">Loading breaches...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Security Breaches</h1>
          <p className="text-sm text-gray-500 mt-1">Monitor and manage data breach incidents</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 72 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                <FaExclamationTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Detected</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDetected || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <FaEnvelope className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Notified</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalNotified || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-orange-100 rounded-md p-3">
                <FaExclamationTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Critical</p>
                <p className="text-2xl font-bold text-gray-900">{stats.bySeverity?.critical || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                <FaExclamationTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">High</p>
                <p className="text-2xl font-bold text-gray-900">{stats.bySeverity?.high || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breaches List */}
      <div className="bg-white shadow overflow-hidden">
        {breaches.length === 0 ? (
          <div className="p-8 text-center">
            <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">No breaches detected in the selected time period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Breach ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Detected
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Indicators
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Affected Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {breaches.map((breach) => (
                  <tr key={breach.breachId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{breach.breachId.substring(0, 20)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(breach.detectedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityColor(breach.severity)}`}>
                        {breach.severity.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {getIndicatorsText(breach.indicators)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {breach.affectedUsers.length} user(s)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {breach.status}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/platform/breaches/${breach.breachId}`)}
                          className="px-3 py-1 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleNotifyUsers(breach.breachId)}
                          disabled={notifyingBreachId === breach.breachId}
                          className="px-3 py-1 text-green-600 bg-green-50 hover:bg-green-100 rounded focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Notify Affected Users"
                        >
                          <FaEnvelope />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {breaches.length > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          <p>⚠️ <strong>GDPR Compliance:</strong> You must notify affected users within 72 hours of breach detection.</p>
        </div>
      )}
    </div>
  );
};

export default PlatformBreaches;

